/**
 * Helpers mínimos de DOM, para escrever UI em vanilla sem framework.
 */
export function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') node.className = v;
    else if (k === 'text') node.textContent = v;
    else if (k === 'html') node.innerHTML = v;
    else if (k === 'dataset') Object.assign(node.dataset, v);
    else if (k.startsWith('on') && typeof v === 'function') {
      node.addEventListener(k.slice(2).toLowerCase(), v);
    } else node.setAttribute(k, v);
  }
  for (const c of [].concat(children)) {
    if (c == null || c === false) continue;
    node.append(c.nodeType ? c : document.createTextNode(String(c)));
  }
  return node;
}

/** Esvazia um elemento (remove todos os filhos). */
export function clear(node) {
  while (node.firstChild) node.removeChild(node.firstChild);
  return node;
}

/** Formata uma data ISO para pt-BR (dd/mm/aaaa hh:mm). Vazio se nulo. */
export function fmtDataHora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Fortaleza' });
}

/** Data (sem hora) em pt-BR (dd/mm/aaaa), no horario do Brasil. Vazio se nulo. */
export function fmtData(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Fortaleza' });
}

/** Tempo decorrido legível em pt-BR ("agora", "há 5 min", "há 2 h", "há 3 d 4 h"). */
export function fmtDecorrido(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return hr ? `há ${d} d ${hr} h` : `há ${d} d`;
}

/** Normaliza texto para busca: remove acentos, baixa caixa e apara espaços. */
export function norm(s = '') {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();
}

const _CONECTIVOS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'di', 'du', 'del', 'la', 'das']);
/**
 * Converte um nome em CAIXA ALTA para caixa normal para exibição ("MARIA DE FATIMA" -> "Maria de Fatima").
 * Não altera o dado guardado; só a apresentação. Mantém conectivos minúsculos.
 */
export function formatarNome(nome = '') {
  return String(nome).toLowerCase().split(/\s+/).filter(Boolean).map((p, i) =>
    (i > 0 && _CONECTIVOS.has(p)) ? p : p.charAt(0).toUpperCase() + p.slice(1),
  ).join(' ');
}

/** Mascara matricula para exibicao (oculta o final). "1670000" -> "167****" */
export function mascararMatricula(m) {
  const s = String(m ?? '').trim();
  if (!s) return '';
  return s.length <= 3 ? s : s.slice(0, 3) + '*'.repeat(s.length - 3);
}

/** Mascara CPF para exibicao. "00001872400" -> "***.018.724-**" */
export function mascararCpf(cpf) {
  const d = String(cpf ?? '').replace(/\D/g, '');
  if (d.length !== 11) return d ? '***' : '';
  return `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**`;
}

/* ---- Documentos: CPF/CNPJ (padronizacao + validacao + mascara ao digitar) ---- */
export function soDigitos(s) { return String(s ?? '').replace(/\D/g, ''); }

/** Primeira parte do nome da empresa (ex.: 'Limpidus Servicos' -> 'Limpidus'). */
export function empresaCurta(s) { return (s || '').trim().split(/\s+/)[0] || ''; }

/** Identificador para CONFIRMAR a pessoa na busca: matricula institucional INTEIRA;
 *  se a matricula for o CPF (terceirizado), mostra o CPF PARCIAL (privacidade no balcao). */
export function rotuloDocumento(c) {
  const md = soDigitos(c && c.matricula), cd = soDigitos(c && c.cpf);
  if (md && md === cd) return 'CPF ' + mascararCpf(c.cpf);
  if (c && c.matricula) return 'mat. ' + c.matricula;
  return cd ? 'CPF ' + mascararCpf(c.cpf) : '';
}

export function formatarCpf(s) {
  const d = soDigitos(s).slice(0, 11);
  return d.length === 11 ? `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}` : d;
}
export function aplicarMascaraCpf(s) {
  const d = soDigitos(s).slice(0, 11);
  if (d.length > 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  if (d.length > 6) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0,3)}.${d.slice(3)}`;
  return d;
}
export function cpfValido(s) {
  const d = soDigitos(s);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  let soma = 0;
  for (let i = 0; i < 9; i++) soma += +d[i] * (10 - i);
  let r = (soma * 10) % 11; if (r === 10) r = 0;
  if (r !== +d[9]) return false;
  soma = 0;
  for (let i = 0; i < 10; i++) soma += +d[i] * (11 - i);
  r = (soma * 10) % 11; if (r === 10) r = 0;
  return r === +d[10];
}

export function formatarCnpj(s) {
  const d = soDigitos(s).slice(0, 14);
  return d.length === 14 ? `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}` : d;
}
export function aplicarMascaraCnpj(s) {
  const d = soDigitos(s).slice(0, 14);
  if (d.length > 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
  if (d.length > 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  if (d.length > 5) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length > 2) return `${d.slice(0,2)}.${d.slice(2)}`;
  return d;
}
export function cnpjValido(s) {
  const d = soDigitos(s);
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const calc = (len) => {
    let soma = 0, pos = len - 7;
    for (let i = len; i >= 1; i--) { soma += +d[len - i] * pos--; if (pos < 2) pos = 9; }
    const r = soma % 11; return r < 2 ? 0 : 11 - r;
  };
  return calc(12) === +d[12] && calc(13) === +d[13];
}

/* ---- Título no padrão do sistema (setores): Title Case, conectivos minúsculos, siglas preservadas ---- */
const _CONECT_TIT = new Set(['de','da','do','das','dos','e','a','o','ao','aos','as','às','à','em','com','para','por','no','na','di','du','del','la','os']);
const _ACRON = new Set(['NIR','CDC','SAME','TI','PIC','UTI','UTIAD','CDPS','CDI','RHC','MAPA','CD','HUOL','UFRN','EBSERH','SEI']);
function _capRun(w) {
  if (_ACRON.has(w.toUpperCase())) return w.toUpperCase();
  if (w.length <= 1) return w.toUpperCase();
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}
/** Title Case no padrão do sistema (conectivos minúsculos, siglas preservadas). Usado nos setores. */
export function formatarTitulo(s = '') {
  return String(s).trim().split(/\s+/).filter(Boolean).map((p, i) => {
    const base = p.replace(/[^\p{L}]/gu, '').toLowerCase();
    if (i > 0 && _CONECT_TIT.has(base)) return p.toLowerCase();
    return p.replace(/\p{L}+/gu, (w) => _capRun(w));
  }).join(' ');
}

/**
 * Envolve um input/textarea com um botão "limpar" (X) que aparece só quando há texto.
 * Ao limpar, foca o campo pronto para digitar de novo. Retorna o wrapper (use no lugar do input).
 */
export function limpavel(input) {
  const x = el('button', { type: 'button', class: 'input-x', tabindex: '-1', title: 'Limpar', 'aria-label': 'Limpar' }, [el('i', { class: 'ph ph-x' })]);
  const atualizar = () => { x.style.display = input.value ? '' : 'none'; };
  x.addEventListener('mousedown', (e) => {
    e.preventDefault();
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    atualizar();
    input.focus();
  });
  input.addEventListener('input', atualizar);
  atualizar();
  return el('div', { class: 'campo-limpavel' }, [input, x]);
}

/** Embrulha um input de senha com um olho para mostrar/ocultar. */
export function olhoSenha(input) {
  const b = el('button', { type: 'button', class: 'input-x', tabindex: '-1', title: 'Mostrar/ocultar senha', 'aria-label': 'Mostrar/ocultar senha' }, [el('i', { class: 'ph ph-eye' })]);
  b.addEventListener('mousedown', (e) => {
    e.preventDefault();
    const mostrar = input.type === 'password';
    input.type = mostrar ? 'text' : 'password';
    if (b.firstChild) b.firstChild.className = mostrar ? 'ph ph-eye-slash' : 'ph ph-eye';
    input.focus();
  });
  return el('div', { class: 'campo-limpavel' }, [input, b]);
}
